/**
 * CommandPalette -- Cmd+K / Ctrl+K global command launcher.
 *
 * A3-2 (Sprint 1): role-aware action list sourced from profile.quick_action_items
 * + standard navigation actions. Uses cmdk primitives for accessibility +
 * keyboard navigation (arrow up/down + Enter).
 *
 * Open via Cmd+K (Mac) or Ctrl+K (Win/Linux). Close via Escape or backdrop click.
 *
 * See: docs/architecture/UX_NAVIGATION_PATTERNS_V2.md S5 (Command Palette)
 */

import { Command } from 'cmdk';
import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigationProfile } from '../context/NavigationContext';

/**
 * Build the action list from profile + standard nav actions.
 * @param {Object} profile - Role profile from /auth/me-style endpoint
 * @param {Function} navigate - page navigation fn
 * @param {Function} t - i18n translation fn
 * @returns {Array<{id, label, hint, onSelect, group}>}
 */
function buildActions(profile, navigate, t) {
  const actions = [];

  // Standard navigation actions (always available)
  const topLevelTabs = profile?.top_level_tabs || ['map', 'farm', 'chat'];
  for (const tab of topLevelTabs) {
    actions.push({
      id: `nav-${tab}`,
      label: t(`nav.${tab}`, tab),
      hint: 'Navigation',
      group: 'Navigation',
      onSelect: () => navigate(`/${tab}`),
    });
  }

  // Quick action items from profile
  const quickActions = profile?.quick_action_items || [];
  for (const qa of quickActions) {
    const id = typeof qa === 'string' ? qa : qa.id;
    const label = typeof qa === 'string' ? t(`quickAction.${qa}`, qa) : qa.label;
    actions.push({
      id: `qa-${id}`,
      label,
      hint: 'Quick action',
      group: 'Quick Actions',
      onSelect: () => navigate(typeof qa === 'string' ? `/${qa}` : qa.href || '/'),
    });
  }

  // Common utility actions
  actions.push(
    { id: 'util-search', label: t('cmd.search', 'Search...'), hint: 'Cmd+/', group: 'Utilities', onSelect: () => navigate('/search') },
    { id: 'util-help', label: t('cmd.help', 'Help'), hint: '?', group: 'Utilities', onSelect: () => navigate('/help') },
    { id: 'util-settings', label: t('cmd.settings', 'Settings'), hint: '', group: 'Utilities', onSelect: () => navigate('/settings') },
  );

  return actions;
}

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onOpenChange
 * @param {Object} props.profile - role profile
 * @param {Function} props.navigate - page navigation fn
 */
export default function CommandPalette({ open, onOpenChange, profile: profileProp, navigate }) {
  const { t } = useTranslation();
  const { profile: ctxProfile } = useNavigationProfile();
  const profile = profileProp ?? ctxProfile;
  const [search, setSearch] = useState('');

  const navFn = useCallback((path) => {
    navigate?.(path);
    onOpenChange(false);
    setSearch('');
  }, [navigate, onOpenChange]);

  const actions = buildActions(profile, navFn, t);

  const handleSelect = useCallback((onSelect) => {
    onSelect?.();
  }, []);

  // Reset search on close
  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  if (!open) return null;

  return (
    <div
      data-testid="command-palette-backdrop"
      onClick={() => onOpenChange(false)}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.4)',
        zIndex: 'var(--aif-z-modal, 1000)',
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        paddingTop: '15vh',
      }}
    >
      <div
        data-testid="command-palette"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: 600,
          background: 'var(--aif-bg-surface, #fff)',
          borderRadius: 12,
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
      >
        <Command label={t('cmd.label', 'Command Palette')}>
          <Command.Input
            value={search}
            onValueChange={setSearch}
            placeholder={t('cmd.placeholder', 'Type a command or search...')}
            data-testid="command-palette-input"
            style={{
              width: '100%',
              padding: '14px 16px',
              border: 'none',
              borderBottom: '1px solid var(--aif-border-light, #eee)',
              outline: 'none',
              fontSize: 16,
              background: 'transparent',
              color: 'var(--aif-text-primary, #111)',
            }}
          />
          <Command.List
            data-testid="command-palette-list"
            style={{ maxHeight: 400, overflowY: 'auto', padding: 8 }}
          >
            <Command.Empty style={{ padding: '12px 16px', color: 'var(--aif-text-muted)' }}>
              {t('cmd.empty', 'No commands found.')}
            </Command.Empty>
            {['Navigation', 'Quick Actions', 'Utilities'].map((groupName) => {
              const groupActions = actions.filter((a) => a.group === groupName);
              if (groupActions.length === 0) return null;
              return (
                <Command.Group key={groupName} heading={groupName}>
                  {groupActions.map((a) => (
                    <Command.Item
                      key={a.id}
                      value={a.label}
                      data-testid={`cmd-${a.id}`}
                      onSelect={() => handleSelect(a.onSelect)}
                      style={{
                        padding: '10px 12px',
                        borderRadius: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'space-between',
                      }}
                    >
                      <span>{a.label}</span>
                      {a.hint && (
                        <span style={{ fontSize: 12, color: 'var(--aif-text-muted)' }}>
                          {a.hint}
                        </span>
                      )}
                    </Command.Item>
                  ))}
                </Command.Group>
              );
            })}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
